const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, Number.isFinite(v) ? v : lo));
const degToRad = (d) => (Number(d) * Math.PI) / 180;
const radToDeg = (r) => (Number(r) * 180) / Math.PI;
const BAY_INTERNAL_ANGLE_MIN = 30;
const BAY_INTERNAL_ANGLE_MAX = 180;

export const BAY_TYPES = {
  L: "L",
  THREE_SIDED: "THREE_SIDED",
  CUSTOM: "CUSTOM",
  CURVED_BOW: "CURVED_BOW",
};

export const BAY_ANGLE_MODES = {
  AUTO: "AUTO",
  MANUAL: "MANUAL",
};

export const BAY_CORNER_DIRECTIONS = {
  LEFT_RETURN: "left-return",
  RIGHT_RETURN: "right-return",
};

const minFacetCountForType = (bayType) => (bayType === BAY_TYPES.CURVED_BOW ? 1 : 2);

const typeFacetCount = (bayType) => {
  if (bayType === BAY_TYPES.L) return 2;
  if (bayType === BAY_TYPES.CURVED_BOW) return 1;
  if (bayType === BAY_TYPES.CUSTOM) return 4;
  return 3;
};

const defaultPrimaryFaceIndex = (bayType, count, cornerDirection = BAY_CORNER_DIRECTIONS.RIGHT_RETURN) => {
  if (count <= 0) return 0;
  if (bayType === BAY_TYPES.L) {
    // For 2-sided corner bays, choose the primary face so the return appears on the requested side.
    return cornerDirection === BAY_CORNER_DIRECTIONS.LEFT_RETURN ? 1 : 0;
  }
  // 3-sided, multi-facet/prism, and curved/bow default to center face.
  return Math.max(0, Math.min(count - 1, Math.floor((count - 1) / 2)));
};

const makeFacetLabel = (idx, total) => {
  if (total === 3 && idx === 0) return "Left return";
  if (total === 3 && idx === 1) return "Centre";
  if (total === 3 && idx === 2) return "Right return";
  return `Facet ${idx + 1}`;
};

export function createDefaultBayConfig({
  tangentWidthMm = 1200,
  heightMm = 1200,
  bayType = BAY_TYPES.THREE_SIDED,
} = {}) {
  const count = typeFacetCount(bayType);
  const projectionDepth = bayType === BAY_TYPES.CURVED_BOW
    ? Math.max(80, Math.round(Number(tangentWidthMm || 0) * 0.22))
    : undefined;
  const widths = (() => {
    if (count === 2) return [Math.max(200, tangentWidthMm * 0.52), Math.max(200, tangentWidthMm * 0.48)];
    if (count === 3) {
      const side = Math.max(220, tangentWidthMm * 0.33);
      const center = Math.max(280, tangentWidthMm * 0.4);
      return [side, center, side];
    }
    if (bayType === BAY_TYPES.CURVED_BOW) {
      return Array.from({ length: count }, (_, i) => {
        const center = (count - 1) / 2;
        const rel = center === 0 ? 0 : Math.abs((i - center) / center);
        const base = tangentWidthMm / count;
        return Math.max(180, base * (1 - rel * 0.16));
      });
    }
    return Array.from({ length: count }, () => Math.max(240, tangentWidthMm / count));
  })();
  const facets = widths.map((w, i) => ({
    id: `facet-${i + 1}`,
    label: makeFacetLabel(i, count),
    width: Math.round(w),
  }));
  const joints = Array.from({ length: Math.max(0, count - 1) }, (_, i) => ({
    id: `joint-${i + 1}`,
    between: [facets[i].id, facets[i + 1].id],
    manualInternalAngle: bayType === BAY_TYPES.L ? 90 : undefined,
  }));
  const angleMode = bayType === BAY_TYPES.L ? BAY_ANGLE_MODES.MANUAL : BAY_ANGLE_MODES.AUTO;
  return {
    bayType,
    cornerDirection: BAY_CORNER_DIRECTIONS.RIGHT_RETURN,
    primaryFaceIndex: defaultPrimaryFaceIndex(bayType, facets.length, BAY_CORNER_DIRECTIONS.RIGHT_RETURN),
    tangentWidth: Math.max(200, Math.round(tangentWidthMm)),
    height: Math.max(200, Math.round(heightMm)),
    angleMode,
    singleFacetAngle: bayType === BAY_TYPES.CURVED_BOW && count === 1 ? 120 : undefined,
    projectionDepth,
    projectionDepthApprox: bayType === BAY_TYPES.CURVED_BOW,
    facets,
    joints,
    derived: { isValid: true, warnings: [] },
  };
}

export function normalizeBayConfig(raw, fallback = {}) {
  const src = raw && typeof raw === "object" ? raw : {};
  const srcBayType = String(src.bayType || "");
  // Backward compatibility: legacy U preset is now represented as 3-sided (with optional 90° returns).
  const compatBayType = srcBayType === "U" ? BAY_TYPES.THREE_SIDED : srcBayType;
  const bayType = Object.values(BAY_TYPES).includes(compatBayType) ? compatBayType : (fallback.bayType || BAY_TYPES.THREE_SIDED);
  const cornerDirection = (src.cornerDirection === BAY_CORNER_DIRECTIONS.LEFT_RETURN || src.cornerDirection === BAY_CORNER_DIRECTIONS.RIGHT_RETURN)
    ? src.cornerDirection
    : ((fallback.cornerDirection === BAY_CORNER_DIRECTIONS.LEFT_RETURN || fallback.cornerDirection === BAY_CORNER_DIRECTIONS.RIGHT_RETURN)
      ? fallback.cornerDirection
      : BAY_CORNER_DIRECTIONS.RIGHT_RETURN);
  const minCount = minFacetCountForType(bayType);
  const count = bayType === BAY_TYPES.CURVED_BOW
    ? 1
    : Math.max(minCount, Math.min(8, Number(src.facets?.length || typeFacetCount(bayType)) || typeFacetCount(bayType)));
  const base = createDefaultBayConfig({
    tangentWidthMm: Number(fallback.tangentWidthMm || src.tangentWidth || 1200),
    heightMm: Number(fallback.heightMm || src.height || 1200),
    bayType,
  });
  const facets = Array.from({ length: count }, (_, i) => {
    const f = src.facets?.[i] || {};
    return {
      id: String(f.id || `facet-${i + 1}`),
      label: String(f.label || makeFacetLabel(i, count)),
      width: Math.max(120, Math.round(Number(f.width || base.facets[i % base.facets.length].width || 400))),
    };
  });
  const joints = Array.from({ length: Math.max(0, count - 1) }, (_, i) => {
    const j = src.joints?.[i] || {};
    const man = Number(j.manualInternalAngle);
    return {
      id: String(j.id || `joint-${i + 1}`),
      between: [facets[i].id, facets[i + 1].id],
      manualInternalAngle: Number.isFinite(man) ? clamp(man, BAY_INTERNAL_ANGLE_MIN, BAY_INTERNAL_ANGLE_MAX) : undefined,
    };
  });
  const angleMode = (() => {
    if (src.angleMode === BAY_ANGLE_MODES.MANUAL) return BAY_ANGLE_MODES.MANUAL;
    if (src.angleMode === BAY_ANGLE_MODES.AUTO) return BAY_ANGLE_MODES.AUTO;
    // 2-sided corner bay defaults to manual 90° behavior.
    if (bayType === BAY_TYPES.L) return BAY_ANGLE_MODES.MANUAL;
    return BAY_ANGLE_MODES.AUTO;
  })();
  const normalizedJoints = joints.map((j) => {
    if (bayType !== BAY_TYPES.L || angleMode !== BAY_ANGLE_MODES.MANUAL) return j;
    return { ...j, manualInternalAngle: Number.isFinite(Number(j.manualInternalAngle)) ? j.manualInternalAngle : 90 };
  });
  const primaryFaceIndexRaw = Number(src.primaryFaceIndex);
  const primaryFaceIndex = Number.isFinite(primaryFaceIndexRaw)
    ? Math.max(0, Math.min(count - 1, Math.round(primaryFaceIndexRaw)))
    : defaultPrimaryFaceIndex(bayType, count, cornerDirection);
  const singleFacetAngleRaw = Number(src.singleFacetAngle);
  const singleFacetAngle = (bayType === BAY_TYPES.CURVED_BOW && count === 1 && Number.isFinite(singleFacetAngleRaw))
    ? clamp(singleFacetAngleRaw, BAY_INTERNAL_ANGLE_MIN, BAY_INTERNAL_ANGLE_MAX)
    : (bayType === BAY_TYPES.CURVED_BOW && count === 1 ? 120 : undefined);
  const projectionDepthRaw = Number(src.projectionDepth);
  const projectionDepthApprox = bayType === BAY_TYPES.CURVED_BOW && count === 1 && !Number.isFinite(projectionDepthRaw);
  const projectionDepth = (bayType === BAY_TYPES.CURVED_BOW && count === 1)
    ? Math.max(20, Number.isFinite(projectionDepthRaw) ? projectionDepthRaw : Number(base.projectionDepth || 120))
    : undefined;
  return {
    bayType,
    cornerDirection,
    primaryFaceIndex,
    tangentWidth: Math.max(200, Math.round(Number(src.tangentWidth || base.tangentWidth))),
    height: Math.max(200, Math.round(Number(src.height || base.height))),
    angleMode,
    singleFacetAngle,
    projectionDepth,
    projectionDepthApprox,
    facets,
    joints: normalizedJoints,
    derived: { isValid: true, warnings: [] },
  };
}

const wrapDeg = (deg) => {
  const n = Number(deg);
  if (!Number.isFinite(n)) return 0;
  return ((n + 180) % 360 + 360) % 360 - 180;
};

// Walk left to right. Each joint turns clockwise by (180 - internal angle),
// the exterior deflection of a bay set in a flat opening.
const walkBayPlan = (widths, internalAngles) => {
  const pts = [{ x: 0, y: 0 }];
  const headings = [];
  let heading = 0;
  for (let i = 0; i < widths.length; i++) {
    headings.push(heading);
    const r = degToRad(heading);
    pts.push({
      x: pts[i].x + widths[i] * Math.cos(r),
      y: pts[i].y + widths[i] * Math.sin(r),
    });
    if (i < internalAngles.length) {
      const internal = clamp(Number(internalAngles[i]), BAY_INTERNAL_ANGLE_MIN, BAY_INTERNAL_ANGLE_MAX);
      heading -= (180 - internal);
    }
  }
  return { pts, headings };
};

// Plan preview steps with (cos(rot), -sin(rot)), so its rotation is the
// negation of the math heading. These rotations are relative to the opening.
const toPreviewRotations = (mathHeadings, originDeg, flip) => mathHeadings.map((h) => {
  let rel = wrapDeg(h - originDeg);
  if (flip) rel = -rel;
  return wrapDeg(-rel);
});

// Opening width is the straight chord between the outer jambs.
const alignPlanToChord = (widths, internalAngles) => {
  const { pts, headings } = walkBayPlan(widths, internalAngles);
  const end = pts[pts.length - 1];
  const chord = Math.hypot(end.x, end.y);
  const chordDeg = radToDeg(Math.atan2(end.y, end.x));
  const rad = degToRad(-chordDeg);
  const cosA = Math.cos(rad);
  const sinA = Math.sin(rad);
  const aligned = pts.map((p) => ({
    x: p.x * cosA - p.y * sinA,
    y: p.x * sinA + p.y * cosA,
  }));
  const maxY = aligned.reduce((m, p) => Math.max(m, p.y), 0);
  const minY = aligned.reduce((m, p) => Math.min(m, p.y), 0);
  const flip = Math.abs(minY) > maxY + 1e-6;
  return {
    chord,
    tangent: chord,
    projection: flip ? Math.abs(minY) : maxY,
    rotations: toPreviewRotations(headings, chordDeg, flip),
  };
};

// A 2-sided corner bay is measured along the primary wall, not the diagonal.
const alignPlanToPrimary = (widths, internalAngles, primaryIndex) => {
  const { headings } = walkBayPlan(widths, internalAngles);
  const pIdx = Math.max(0, Math.min(widths.length - 1, Number(primaryIndex) || 0));
  const origin = headings[pIdx] || 0;
  const rel = headings.map((h) => wrapDeg(h - origin));
  let y = 0;
  let projection = 0;
  let tangent = 0;
  for (let i = 0; i < widths.length; i++) {
    const r = degToRad(rel[i]);
    tangent += widths[i] * Math.cos(r);
    y += widths[i] * Math.sin(r);
    projection = Math.max(projection, Math.abs(y));
  }
  return {
    chord: Math.hypot(tangent, y),
    tangent: Math.abs(tangent),
    projection,
    rotations: toPreviewRotations(headings, origin, false),
  };
};

const planForAngles = (widths, internalAngles, usePrimaryTangent, primaryIndex) => (
  usePrimaryTangent
    ? alignPlanToPrimary(widths, internalAngles, primaryIndex)
    : alignPlanToChord(widths, internalAngles)
);

// Tangent-driven mode has one unknown, so every joint shares the internal angle.
// The shallowest bay that hits the opening is the one surveyors mean.
const solveEqualInternalAngle = (widths, targetSpan, usePrimaryTangent, primaryIndex) => {
  const jointCount = Math.max(0, widths.length - 1);
  const measure = (angle) => planForAngles(widths, Array(jointCount).fill(angle), usePrimaryTangent, primaryIndex);
  const spanOf = (plan) => Number(plan?.tangent || 0);
  const flat = measure(BAY_INTERNAL_ANGLE_MAX);
  const flatSpan = spanOf(flat);
  if (!(targetSpan <= flatSpan + 0.5)) {
    return { ok: false, angle: BAY_INTERNAL_ANGLE_MAX, plan: flat };
  }
  // Span is not monotonic in the angle once there are 4+ facets (the plan folds back on
  // itself), so plain bisection over the whole range can land on a self-crossing plan.
  // Walk down from flat to the first angle that reaches the target, then bisect that bracket.
  const SCAN_STEP_DEG = 0.5;
  let best = flat;
  let bestDist = Math.abs(flatSpan - targetSpan);
  let hi = BAY_INTERNAL_ANGLE_MAX;
  let lo = null;
  for (let a = BAY_INTERNAL_ANGLE_MAX - SCAN_STEP_DEG; a >= BAY_INTERNAL_ANGLE_MIN - 1e-9; a -= SCAN_STEP_DEG) {
    const plan = measure(a);
    const span = spanOf(plan);
    const dist = Math.abs(span - targetSpan);
    if (dist < bestDist) {
      best = plan;
      bestDist = dist;
    }
    if (span <= targetSpan) { lo = a; break; }
    hi = a;
  }
  if (lo == null) return { ok: bestDist <= 0.5, angle: hi, plan: best };
  for (let i = 0; i < 40; i++) {
    const mid = (lo + hi) / 2;
    const plan = measure(mid);
    const span = spanOf(plan);
    const dist = Math.abs(span - targetSpan);
    if (dist < bestDist) {
      best = plan;
      bestDist = dist;
    }
    if (span > targetSpan) hi = mid;
    else lo = mid;
  }
  const angle = (lo + hi) / 2;
  const plan = measure(angle);
  const ok = Math.abs(spanOf(plan) - targetSpan) <= 0.5;
  return { ok, angle, plan: ok ? plan : best };
};

export function computeBayGeometry(configRaw) {
  const config = normalizeBayConfig(configRaw);
  const warnings = [];
  const facets = config.facets.map((f) => ({ ...f }));
  const tangentWidth = Number(config.tangentWidth);
  const count = facets.length;
  const rotations = Array.from({ length: count }, () => 0);
  let isValid = true;
  let curvedRadius = null;
  let curvedArcLength = null;
  let curvedProjection = null;
  let hasCurvedFace = false;
  let openingTangent = tangentWidth;
  let planProjection = 0;
  const widths = facets.map((f) => Number(f.width) || 0);
  const primaryFaceIndex = defaultPrimaryFaceIndex(config.bayType, count, config.cornerDirection);
  const usePrimaryTangent = config.bayType === BAY_TYPES.L && count === 2;

  if (count === 1 && config.bayType === BAY_TYPES.CURVED_BOW) {
    hasCurvedFace = true;
    const chord = Math.max(1, tangentWidth);
    const maxSagitta = Math.max(1, chord / 2);
    const sagittaRaw = Number(config.projectionDepth);
    const sagitta = clamp(
      Number.isFinite(sagittaRaw) ? sagittaRaw : (Math.max(40, chord * 0.22)),
      1,
      maxSagitta
    );
    curvedProjection = sagitta;
    curvedRadius = ((chord * chord) / (8 * sagitta)) + (sagitta / 2);
    const ratio = clamp(chord / (2 * Math.max(1, curvedRadius)), -1, 1);
    const theta = 2 * Math.asin(ratio);
    curvedArcLength = Math.max(chord, curvedRadius * theta);
    rotations[0] = 0;
    if (config.projectionDepthApprox) {
      warnings.push("Projection depth is approximated. Set Projection Depth for an exact curved plan.");
    }
    planProjection = Number(curvedProjection || 0);
  } else if (config.angleMode === BAY_ANGLE_MODES.MANUAL && config.joints.length) {
    const angles = [];
    let missing = false;
    for (let i = 0; i < config.joints.length; i++) {
      const manual = Number(config.joints[i].manualInternalAngle);
      if (!Number.isFinite(manual)) {
        missing = true;
        break;
      }
      angles.push(clamp(manual, BAY_INTERNAL_ANGLE_MIN, BAY_INTERNAL_ANGLE_MAX));
    }
    if (missing) {
      warnings.push("Manual mode requires internal angles for each bay joint.");
      isValid = false;
    } else {
      const plan = planForAngles(widths, angles, usePrimaryTangent, primaryFaceIndex);
      plan.rotations.forEach((rot, i) => { rotations[i] = rot; });
      openingTangent = plan.tangent;
      planProjection = plan.projection;
    }
  } else if (count > 1) {
    const solved = solveEqualInternalAngle(widths, tangentWidth, usePrimaryTangent, primaryFaceIndex);
    if (solved.plan) {
      solved.plan.rotations.forEach((rot, i) => { rotations[i] = rot; });
      openingTangent = solved.plan.tangent;
      planProjection = solved.plan.projection;
    }
    if (!solved.ok) {
      warnings.push("The supplied tangent width cannot form a valid bay with these facet widths.");
      isValid = false;
    }
  }

  const projection = hasCurvedFace
    ? Number(curvedProjection || 0)
    : planProjection;
  const visualWidths = facets.map((f, i) => Math.max(28, f.width * Math.max(0.14, Math.cos(degToRad(Math.abs(rotations[i]))))));
  const visualSum = visualWidths.reduce((s, v) => s + v, 0) || 1;

  const joints = hasCurvedFace
    ? []
    : config.joints.map((j, i) => {
    const delta = Math.abs(wrapDeg((rotations[i + 1] || 0) - (rotations[i] || 0)));
    const derivedInternalAngle = clamp(180 - delta, BAY_INTERNAL_ANGLE_MIN, BAY_INTERNAL_ANGLE_MAX);
    return {
      ...j,
      derivedInternalAngle,
      rotationDelta: delta,
      manualInternalAngle: config.angleMode === BAY_ANGLE_MODES.MANUAL ? j.manualInternalAngle : undefined,
    };
  });

  let cx = 0;
  const vertices = facets.map((f, i) => {
    const vw = visualWidths[i];
    const x0 = cx;
    const x1 = cx + vw;
    cx = x1;
    return {
      facetId: f.id,
      polygon: [
        { x: x0 / visualSum, y: 0 },
        { x: x1 / visualSum, y: 0 },
        { x: x1 / visualSum, y: 1 },
        { x: x0 / visualSum, y: 1 },
      ],
    };
  });
  const derivedTangentWidth = hasCurvedFace ? tangentWidth : openingTangent;

  return {
    config: { ...config, primaryFaceIndex },
    primaryFaceIndex,
    facets: facets.map((f, i) => ({
      ...f,
      rotation: rotations[i],
      visualWidth: visualWidths[i],
    })),
    faces: facets.map((f, i) => ({
      ...f,
      rotation: rotations[i],
      visualWidth: visualWidths[i],
    })),
    joints,
    derived: {
      projection,
      tangentWidth: derivedTangentWidth,
      radius: curvedRadius,
      arcLength: curvedArcLength,
      hasCurvedFace,
      projectionApprox: !!config.projectionDepthApprox,
      isValid,
      warnings,
    },
    planVertices: vertices,
  };
}
